import React, { useRef, useEffect, KeyboardEvent, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface ChatInputProps {
  input: string;
  isLoading: boolean;
  onInputChange: (value: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  hasMessages: boolean;
  responseStyle?: 'concise' | 'normal' | 'descriptive';
  onResponseStyleChange?: (style: 'concise' | 'normal' | 'descriptive') => void;
  sourceFilter?: string[];
  onSourceFilterChange?: (sources: string[]) => void;
  userCompanyId?: number | null;
}

export default function ChatInput({ 
  input, 
  isLoading, 
  onInputChange, 
  onSubmit, 
  hasMessages,
  responseStyle = 'normal',
  onResponseStyleChange,
  sourceFilter = [],
  onSourceFilterChange,
  userCompanyId
}: ChatInputProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  // Simple state for center vs bottom positioning
  const [isAtBottom, setIsAtBottom] = useState(hasMessages);
  // Remove dropdown states - no longer needed

  // Handle keyboard events in textarea
  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onSubmit(e as any);
    }
  };

  // Auto-resize textarea
  const autoResizeTextarea = () => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 120) + 'px';
    }
  };

  useEffect(() => {
    autoResizeTextarea();
  }, [input]);

  // Handle position changes
  useEffect(() => {
    setIsAtBottom(hasMessages);
  }, [hasMessages]);

  // Remove dropdown click handlers - no longer needed

  // Remove filter options - no longer needed

  // Animation variants for the wrapper
  const wrapperVariants = {
    center: {
      borderTop: '1rem',
      padding: '0',
      background: 'transparent'
    },
    bottom: {
      borderTop: '0',
      padding: '1rem',
      transition: {
        type: 'spring',
        stiffness: 300,
        damping: 30
      }
    }
  };

  // Animation variants for the container - simple vertical movement
  const containerVariants = {
    center: {
      position: 'absolute' as const,
      top: '50%',
      left: '0',
      right: '0',
      transform: 'translateY(-50%)',
      zIndex: 50,
      transition: {
        type: 'spring',
        stiffness: 300,
        damping: 30
      }
    },
    bottom: {
      position: 'static' as const,
      top: 'auto',
      left: 'auto',
      right: 'auto',
      transform: 'none',
      zIndex: 'auto',
      transition: {
        type: 'spring',
        stiffness: 300,
        damping: 30
      }
    }
  };

  return (
    <motion.div 
      className=""
      variants={wrapperVariants}
      animate={isAtBottom ? 'bottom' : 'center'}
      initial={false}
  
    >
      <motion.div 
        variants={containerVariants}
        animate={isAtBottom ? 'bottom' : 'center'}
        initial={false}
      >
        <div className={isAtBottom ? "max-w-7xl mx-auto w-full px-2 sm:px-4 lg:px-0" : "mx-auto w-full px-2 sm:px-4 lg:px-0 flex flex-col items-center"}>
        {/* Welcome message for new conversations */}
        {!hasMessages && (
          <div className="mb-6 text-center">
            <h2 className="text-2xl font-bold theme-text">
              How can I assist you today?
            </h2>
          </div>
        )}
        
        {/* Main input container */}
        <motion.div 
          className={"theme-bg-secondary rounded-3xl border theme-border shadow-sm focus-within:shadow-md transition-all duration-200 w-full" + (isAtBottom ? ' max-w-7xl' : ' max-w-4xl min-w-0 sm:min-w-[600px] lg:min-w-[800px]')}

          initial={false}
          animate={{ 
            scale: 1, 
            opacity: 1,
            boxShadow: !isAtBottom
              ? '0 25px 50px -12px rgb(0 0 0 / 0.25)'
              : '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)'
          }}
          transition={{ 
            type: 'spring',
            stiffness: 300,
            damping: 30
          }}
        >
          <form onSubmit={onSubmit}>
            {/* Input area */}
            <div className="flex items-start p-4">
              
              {/* Text input */}
              <div className="flex-1 min-w-0">
                <textarea
                  ref={textareaRef}
                  value={input}
                  onChange={(e) => onInputChange(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Type your prompt here..."
                  className="w-full bg-transparent border-0 focus:outline-none resize-none theme-text placeholder:theme-text-muted text-base leading-relaxed text-sm"
                  rows={1}
                  style={{ minHeight: '24px', maxHeight: '120px' }}
                />
              </div>
              
              {/* Send button */}
              <div className="ml-3 mt-1">
                <motion.button 
                  type="submit" 
                  className={`p-2.5 rounded-full transition-all duration-200 ${
                    isLoading || !input.trim() 
                      ? 'theme-text-muted theme-bg-tertiary cursor-not-allowed' 
                      : 'text-white theme-primary theme-primary-hover shadow-lg hover:shadow-xl'
                  }`}
                  disabled={isLoading || !input.trim()}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {isLoading ? (
                    <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                    </svg>
                  )}
                </motion.button>
              </div>
            </div>
            
            {/* Bottom info bar */}
            <motion.div 
              className="flex flex-col lg:flex-row lg:items-center lg:justify-between px-2 sm:px-4 pb-3 pt-3 border-t theme-border space-y-2 lg:space-y-0"
              initial={false}
              animate={{ opacity: 1 }}
            >
              <div className="flex flex-wrap items-center gap-1 sm:gap-2 lg:gap-4 text-xs">
                {/* Response Style Selector */}
                <div className="flex items-center space-x-1 sm:space-x-2">
                  <label className="text-[10px] sm:text-xs theme-text-muted whitespace-nowrap" htmlFor="response-style">
                    Style:
                  </label>
                  <div className="flex theme-bg-tertiary rounded-xl p-1 py-1">
                    {(['concise', 'normal', 'descriptive'] as const).map((style) => (
                      <button
                        key={style}
                        type="button"
                        onClick={() => onResponseStyleChange?.(style)}
                        className={`px-1.5 sm:px-2 py-0 text-[9px] sm:text-[10px] rounded-xl transition-colors ${
                          responseStyle === style
                            ? 'theme-primary theme-text-inverse'
                            : 'theme-text-secondary hover:theme-text'
                        }`}
                      >
                        {style.charAt(0).toUpperCase() + style.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Source Filter Selector - Only show for company ID 1 */}
                {userCompanyId === 1 && (
                  <div className="flex items-center space-x-1 sm:space-x-2">
                    <label className="text-[10px] sm:text-xs theme-text-muted whitespace-nowrap" htmlFor="source-filter">
                      Sources:
                    </label>
                    <div className="flex theme-bg-tertiary rounded-xl p-1 py-1">
                      {(['ACAS', 'Policy'] as const).map((source, index) => {
                        const isSelected = sourceFilter.includes(source);
                        const isFirstSelected = sourceFilter.includes('ACAS');
                        const isSecondSelected = sourceFilter.includes('Policy');
                        const bothSelected = isFirstSelected && isSecondSelected;
                        
                        // Dynamic border radius based on selection state
                        let borderRadius = '';
                        if (index === 0) { // ACAS button
                          if (bothSelected) {
                            borderRadius = 'rounded-l-xl'; // Only left side rounded when both selected
                          } else {
                            borderRadius = 'rounded-xl'; // Fully rounded when only this one or none selected
                          }
                        } else { // Policy button
                          if (bothSelected) {
                            borderRadius = 'rounded-r-xl'; // Only right side rounded when both selected
                          } else {
                            borderRadius = 'rounded-xl'; // Fully rounded when only this one or none selected
                          }
                        }
                        
                        return (
                          <button
                            key={source}
                            type="button"
                            onClick={() => {
                              if (!onSourceFilterChange) return;
                              if (isSelected) {
                                // Prevent removing if it's the only selected option
                                if (sourceFilter.length > 1) {
                                  // Remove from selection only if there's more than one selected
                                  onSourceFilterChange(sourceFilter.filter(s => s !== source));
                                }
                                // If only one is selected, do nothing (prevent deselection)
                              } else {
                                // Add to selection
                                onSourceFilterChange([...sourceFilter, source]);
                              }
                            }}
                            className={`px-1.5 sm:px-2 py-0 text-[9px] sm:text-[10px] ${borderRadius} transition-colors ${
                              isSelected
                                ? 'theme-primary theme-text-inverse'
                                : 'theme-text-secondary hover:theme-text'
                            }`}
                          >
                            {source}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
              
              <div className="text-[10px] theme-text-muted hidden lg:block">
                Press <kbd className="px-1.5 py-0.5 theme-bg-tertiary rounded text-[10px] font-mono theme-text-secondary">Shift+Enter</kbd> for new line
              </div>
            </motion.div>
          </form>
        </motion.div>
        </div>
      </motion.div>
    </motion.div>
  );
}
